import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReportPageIcon from '../../assets/icons/reports-active.svg';
import DownloadIcon from '../../assets/icons/download-cloud-02.svg';
import Button from '../../components/Button/Button';
import axiosInstance from '../../utils/axiosInstance';
import openNotification from '../../components/Common/OpenNotification';
import { PATHNAME } from '../../pathname';

const ReportsPage = () => {
        const navigate = useNavigate();
        const [downloading, setDownloading] = useState({});

        const reportTypes = [
                {
                        id: 'project-reports',
                        title: 'Project Reports',
                        description: 'Download comprehensive project reports with detailed project information and status',
                        endpoint: '/api/admin/project-report-download',
                        filename: 'Project_Reports.xlsx',
                },
                {
                        id: 'project-report-unit-wise',
                        title: 'Project Report Unit Wise',
                        description: 'Download project reports organized by unit with unit-specific project details',
                        endpoint: '/api/admin/project-report-unit-wise',
                        filename: 'Project_Report_Unit_Wise.xlsx',
                },
                {
                        id: 'unit-report-project-wise',
                        title: 'Unit / Region Report Project Wise',
                        description: 'Download unit reports organized by project with project-specific unit information',
                        endpoint: '/api/admin/unit-report-project-wise',
                        filename: 'Unit/Region_Report_Project_Wise.xlsx',
                },
               
                {
                        id: 'unit-report-package-wise',
                        title: 'Unit / Region Report Package Wise',
                        description: 'Download unit reports organized by package with package-specific unit information',
                        endpoint: '/api/admin/unit-report-package-wise',
                        filename: 'Unit / Region_Report_Package_Wise.xlsx',
                },
                {
                        id: 'project-report-package-wise',
                        title: 'Project Report Package Wise',
                        description: 'Download project reports organized by package with package-specific project details',
                        endpoint: '/api/admin/project-report-package-wise',
                        filename: 'Project_Report_Package_Wise.xlsx',
                },
        ];

        const handleDownloadReport = async (reportType) => {
                const report = reportTypes.find((r) => r.id === reportType);
                if (!report) return;

                setDownloading((prev) => ({ ...prev, [reportType]: true }));

                try {
                        openNotification('info', 'Download', 'Downloading file in progress...');

                        const response = await axiosInstance.get(report.endpoint, {
                                responseType: 'blob',
                        });

                        // Create blob and download
                        const blob = new Blob([response.data], {
                                type: response.data.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        });

                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = report.filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);

                        openNotification('success', 'Download Complete', `${report.title} downloaded successfully`);
                } catch (error) {
                        console.error('Download failed:', error);
                        openNotification('error', 'Download Failed', error?.response?.data?.message || 'Something went wrong while downloading the report.');
                } finally {
                        setDownloading((prev) => ({ ...prev, [reportType]: false }));
                }
        };

        return (
                <div className="pb-14">
                        <div className="px-5 flex flex-col gap-6 pt-8">
                                <div className="flex gap-3">
                                        <img
                                                src={ReportPageIcon}
                                                alt="reports"
                                        />
                                        <div>
                                                <div className="font-inter font-semibold text-[24px] leading-[32px] tracking-[0%] text-[#101828]">Reports</div>
                                                <div className="font-inter font-normal text-[14px] leading-[24px] tracking-[0%] text-[#475467]">Download various types of reports for your organization</div>
                                        </div>
                                </div>

                                {/* Navigable Report Cards */}
                                <div className="mt-6">
                                        <h2 className="font-inter font-semibold text-[20px] leading-[28px] tracking-[0%] text-[#101828] mb-4">Report Views</h2>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div
                                                        onClick={() => navigate(PATHNAME.ADMIN_UNIT_LEVEL_REPORT)}
                                                        className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-lg hover:border-blue-300 transition-all duration-200 cursor-pointer">
                                                        <div className="flex flex-col gap-4">
                                                                <div>
                                                                        <h3 className="font-inter font-semibold text-[18px] leading-[24px] tracking-[0%] text-[#101828] mb-2">Unit / Region Level Report</h3>
                                                                        <p className="font-inter font-normal text-[14px] leading-[20px] tracking-[0%] text-[#475467]">View comprehensive unit-level reports with detailed unit information and analytics</p>
                                                                </div>
                                                                <div className="mt-auto flex items-center text-blue-600 font-inter font-medium text-[14px]">
                                                                        <span>View Report</span>
                                                                        <svg
                                                                                className="w-5 h-5 ml-2"
                                                                                fill="none"
                                                                                stroke="currentColor"
                                                                                viewBox="0 0 24 24">
                                                                                <path
                                                                                        strokeLinecap="round"
                                                                                        strokeLinejoin="round"
                                                                                        strokeWidth={2}
                                                                                        d="M9 5l7 7-7 7"
                                                                                />
                                                                        </svg>
                                                                </div>
                                                        </div>
                                                </div>

                                                <div
                                                        onClick={() => navigate(PATHNAME.ADMIN_PROJECT_LEVEL_REPORT)}
                                                        className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-lg hover:border-blue-300 transition-all duration-200 cursor-pointer">
                                                        <div className="flex flex-col gap-4">
                                                                <div>
                                                                        <h3 className="font-inter font-semibold text-[18px] leading-[24px] tracking-[0%] text-[#101828] mb-2">Project Level Report</h3>
                                                                        <p className="font-inter font-normal text-[14px] leading-[20px] tracking-[0%] text-[#475467]">View comprehensive project-level reports with detailed project information and analytics</p>
                                                                </div>
                                                                <div className="mt-auto flex items-center text-blue-600 font-inter font-medium text-[14px]">
                                                                        <span>View Report</span>
                                                                        <svg
                                                                                className="w-5 h-5 ml-2"
                                                                                fill="none"
                                                                                stroke="currentColor"
                                                                                viewBox="0 0 24 24">
                                                                                <path
                                                                                        strokeLinecap="round"
                                                                                        strokeLinejoin="round"
                                                                                        strokeWidth={2}
                                                                                        d="M9 5l7 7-7 7"
                                                                                />
                                                                        </svg>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                </div>

                                {/* Downloadable Reports */}
                                <div className="mt-8">
                                        <h2 className="font-inter font-semibold text-[20px] leading-[28px] tracking-[0%] text-[#101828] mb-4">Export Reports</h2>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                {reportTypes.map((report) => (
                                                        <div
                                                                key={report.id}
                                                                className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
                                                                <div className="flex flex-col gap-4">
                                                                        <div>
                                                                                <h3 className="font-inter font-semibold text-[18px] leading-[24px] tracking-[0%] text-[#101828] mb-2">{report.title}</h3>
                                                                                <p className="font-inter font-normal text-[14px] leading-[20px] tracking-[0%] text-[#475467]">{report.description}</p>
                                                                        </div>
                                                                        <div className="mt-auto">
                                                                                <Button
                                                                                        text={downloading[report.id] ? 'Downloading...' : 'Export report'}
                                                                                        icon={DownloadIcon}
                                                                                        onClick={() => handleDownloadReport(report.id)}
                                                                                        width="100%"
                                                                                        disabled={downloading[report.id]}
                                                                                />
                                                                        </div>
                                                                </div>
                                                        </div>
                                                ))}
                                        </div>
                                </div>
                        </div>
                </div>
        );
};

export default ReportsPage;
